import SockJS from 'sockjs-client';
import Stomp from 'stompjs';
// import httpBaseConfig from './server/http';

// 未显式传 owner 的旧调用统一使用这个保留标识：
// 同一 destination 上后来者替换前者，与改造前的行为完全一致（详见 subscribe 的注释）。
const LEGACY_OWNER = '__legacy__';

/**
 * 订阅登记表。
 *
 * 之所以放在模块级而不是实例字段：header/index.js 在断线重连时会 new 一个 WebSocketInit，
 * 登记表若属于实例，新实例连上后 replaySubs 永远是空的，已打开页面的订阅无法自动恢复
 * （后端重启后详情页位置推送静默失效、必须重开页面，就是这个原因）。
 */
const registry = {
  // destination -> { sub: stomp 订阅句柄, callbacks: Map<owner, 回调> }
  subs: new Map(),
  // relationKey -> { dest, sendUrl, unsubscribeUrl, headers, requestStr, owners: Set<owner> }
  // 一个 destination 上可以登记多条"服务端订阅关系"（例如详情页看 A、叉车页看 B），
  // 关系按 owners 做引用计数，计数归零才真正下发退订报文。
  relations: new Map(),
  // owner -> Set<relationKey>：退订时反查该订阅者登记过哪些关系
  ownerRelations: new Map(),
};

const relationKey = (dest, sendUrl, requestStr) =>
  `${dest || ''}|${sendUrl}|${JSON.stringify(requestStr === undefined ? null : requestStr)}`;

const trackOwnerRelation = (owner, key) => {
  let keys = registry.ownerRelations.get(owner);
  if (!keys) {
    keys = new Set();
    registry.ownerRelations.set(owner, keys);
  }
  keys.add(key);
};

const untrackOwnerRelation = (owner, key) => {
  const keys = registry.ownerRelations.get(owner);
  if (!keys) {
    return;
  }
  keys.delete(key);
  if (keys.size === 0) {
    registry.ownerRelations.delete(owner);
  }
};

const clearRegistry = () => {
  registry.subs.clear();
  registry.relations.clear();
  registry.ownerRelations.clear();
};

class WebSocketInit {
  constructor () {
    this.socket = null;
    this.url = '';
    this.conFlag = false;
    this.stompClient = null;
    this.reconnectionState = false;
  }

  init (url, headers, success, close) {
    const $this = this;
    $this.url = url;
    $this.socket = new SockJS($this.url);
    $this.stompClient = Stomp.over($this.socket);
    // 连接都还没建立就失败（后端重启、网关拒绝等）也要能通知外层去重连
    if (typeof close === 'function') {
      $this.socket.onclose = () => close();
    }
    $this.stompClient.connect(headers, () => {
      $this.conFlag = true;
      // 断线重连后，把登记过的订阅（本地订阅 + 服务端关系）全部补回来
      $this.replaySubs();
      if (typeof success === 'function') {
        success();
      }
      if (typeof close === 'function') {
        $this.socket.onclose = () => close();
      }
    });
  }

  send (url, headers, requestStr) {
    if (!url) {
      // 改造前 sendUrl 为空时也会发出一条 destination 为空的 STOMP SEND（detail.js / sendSms 的调用），
      // 没有意义，这里直接收口
      return;
    }
    this.stompClient.send(url, headers, JSON.stringify(requestStr === undefined ? null : requestStr));
  }

  isReady () {
    return !!(this.conFlag && this.stompClient && this.stompClient.connected);
  }

  /**
   * 订阅（可附带登记一条"服务端订阅关系"）
   *
   * @param headers        STOMP headers（access_token）
   * @param subUrl         本地订阅目的地；传 null 表示只登记服务端关系、不在本地订阅
   * @param callBack       推送回调
   * @param sendUrl        建立服务端关系的目的地（如 /app/subscribe/gpsInfo）；传 null 表示无关系
   * @param requestStr     关系报文内容
   * @param options        { owner, unsubscribeUrl }
   *   owner          订阅者标识。**不传则退化为改造前的"同一 destination 后来者替换"语义**；
   *                  传了则同一 (subUrl, owner) 重复调用只更新回调，多个 owner 各自都能收到推送。
   *                  必须是稳定标识（组件实例级），不能用每次 render 都会变的回调引用代替。
   *   unsubscribeUrl 取消关系的目的地。必须显式传，不能由 sendUrl 推导
   *                  （deviceInstruction 的取消是 /app/subscribe/deviceInstruction/cancel）。
   */
  subscribe (headers, subUrl, callBack, sendUrl, requestStr, options) {
    const { owner = LEGACY_OWNER, unsubscribeUrl = null } = options || {};

    // ① 本地订阅登记
    if (subUrl) {
      let entry = registry.subs.get(subUrl);
      if (!entry) {
        entry = { sub: null, callbacks: new Map() };
        registry.subs.set(subUrl, entry);
      }
      // 同一 owner 覆盖回调：默认（LEGACY_OWNER）因此保持"后来者替换"的旧行为，
      // 也避免了拿每次 render 都新建的函数做 key 时条目无限膨胀
      entry.callbacks.set(owner, callBack);
      this.ensureStompSub(subUrl);
    }

    // ② 服务端关系登记：同一 owner 在同一 destination 上只保留最后一条
    if (sendUrl) {
      this.releaseRelations(subUrl, owner, false);
      const key = relationKey(subUrl, sendUrl, requestStr);
      let relation = registry.relations.get(key);
      if (!relation) {
        relation = { dest: subUrl, sendUrl, unsubscribeUrl, headers, requestStr, owners: new Set() };
        registry.relations.set(key, relation);
      } else if (unsubscribeUrl) {
        relation.unsubscribeUrl = unsubscribeUrl;
      }
      relation.owners.add(owner);
      trackOwnerRelation(owner, key);
      if (this.isReady()) {
        this.send(relation.sendUrl, relation.headers, relation.requestStr);
      }
    }

    // ③ 通道未就绪时只登记，连上后由 replaySubs 统一落地
    if (!this.isReady()) {
      this.ensureConnection(headers);
    }
  }

  /**
   * 取消订阅
   *
   * @param subUrl 目的地
   * @param owner  订阅者标识；省略表示移除该 destination 上的所有订阅者（保留改造前语义）
   */
  unsubscribe (subUrl, owner) {
    const entry = registry.subs.get(subUrl);
    if (entry) {
      if (owner === undefined) {
        entry.callbacks.clear();
      } else {
        entry.callbacks.delete(owner);
      }
      if (entry.callbacks.size === 0) {
        this.teardownStompSub(entry);
        registry.subs.delete(subUrl);
      }
    }
    this.releaseRelations(subUrl, owner, true);
  }

  /**
   * 摘掉某订阅者在指定 destination 上登记的服务端关系
   *
   * @param notify true 时，某条关系的引用计数归零会下发 unsubscribeUrl（真正的退订）
   * @return 被彻底移除的关系列表
   */
  releaseRelations (dest, owner, notify) {
    const keys = [];
    if (owner === undefined) {
      registry.relations.forEach((relation, key) => {
        if (relation.dest === dest) {
          keys.push(key);
        }
      });
    } else {
      (registry.ownerRelations.get(owner) || []).forEach((key) => {
        const relation = registry.relations.get(key);
        if (relation && relation.dest === dest) {
          keys.push(key);
        }
      });
    }
    const removed = [];
    keys.forEach((key) => {
      const relation = registry.relations.get(key);
      if (!relation) {
        return;
      }
      if (owner === undefined) {
        Array.from(relation.owners).forEach((other) => untrackOwnerRelation(other, key));
        relation.owners.clear();
      } else {
        relation.owners.delete(owner);
        untrackOwnerRelation(owner, key);
      }
      if (relation.owners.size > 0) {
        // 还有别的页面在用这条关系，不能退订
        return;
      }
      if (notify && relation.unsubscribeUrl) {
        this.send(relation.unsubscribeUrl, relation.headers, relation.requestStr);
      }
      registry.relations.delete(key);
      removed.push(relation);
    });
    return removed;
  }

  /**
   * 一个 destination 只保留一条 STOMP 订阅，多回调在本地扇出。
   * 若按回调条数各订阅一次，服务端同一帧会被投递 N 次，回调被调用 N² 次。
   */
  ensureStompSub (subUrl) {
    const entry = registry.subs.get(subUrl);
    if (!entry || !this.isReady()) {
      return;
    }
    // entry.sub 必须属于当前连接：登记表是模块级的，重连换了实例之后
    // 这里可能还留着上一个连接的句柄，直接当"已订阅"会静默收不到推送
    if (entry.sub && entry.client === this.stompClient) {
      return;
    }
    if (entry.sub) {
      this.teardownStompSub(entry);
    }
    entry.client = this.stompClient;
    entry.sub = this.stompClient.subscribe(subUrl, (frame) => this.dispatch(subUrl, frame));
  }

  teardownStompSub (entry) {
    if (entry && entry.sub) {
      try {
        entry.sub.unsubscribe();
      } catch (e) {
        // 连接已断开时取消失败可以忽略
      }
      entry.sub = null;
      entry.client = null;
    }
  }

  /**
   * 本地扇出：任一回调抛异常不能影响同一帧的其它订阅者
   */
  dispatch (subUrl, frame) {
    const entry = registry.subs.get(subUrl);
    if (!entry) {
      return;
    }
    entry.callbacks.forEach((callBack, owner) => {
      try {
        callBack(frame);
      } catch (e) {
        console.error(`[socket] 推送回调异常; destination=${subUrl}; owner=${owner}`, e);
      }
    });
  }

  /**
   * 重建全部订阅（连接建立/重连后调用）
   */
  replaySubs () {
    // 旧连接上的订阅句柄已失效，必须清掉，否则 ensureStompSub 会误判为已订阅
    registry.subs.forEach((entry) => {
      entry.sub = null;
      entry.client = null;
    });
    registry.subs.forEach((entry, subUrl) => this.ensureStompSub(subUrl));
    registry.relations.forEach((relation) => {
      if (relation.owners.size > 0) {
        this.send(relation.sendUrl, relation.headers, relation.requestStr);
      }
    });
  }

  /**
   * 通道未就绪时补建连接。
   * 正常情况下连接生命周期由 header 统一管理，这里只是兜底。
   */
  ensureConnection (headers) {
    if (this.isReady() || this.reconnectionState || !this.url) {
      return;
    }
    const $this = this;
    $this.reconnectionState = true;
    $this.socket = new SockJS($this.url);
    $this.stompClient = Stomp.over($this.socket);
    $this.stompClient.connect(headers, () => {
      $this.conFlag = true;
      $this.reconnectionState = false;
      $this.replaySubs();
    });
  }

  unsubscribealarm (headers, url, requestStr) {
    this.stompClient.send(url, headers, JSON.stringify(requestStr));
  }

  abort (headers, url) {
    this.stompClient.disconnect(url, headers);
  }

  close () {
    if (!(this.socket === null)) {
      this.socket.close();
      this.socket = null;
      this.stompClient = null;
      this.url = '';
      this.conFlag = false;
      this.reconnectionState = false;
      // 退出登录必须清空登记表，否则重新登录后会把上一次会话的订阅重放出来
      clearRegistry();
    }
  }
}

export default WebSocketInit;
